import { httpsCallable } from "firebase/functions";
import { SUBMIT_CLIENT_TIMEOUT_MS, type TierId, type SubmissionSource } from "@ks-gen/shared";
import type { FormState } from "../state";
import { getFirebaseFunctions } from "./firebase";
import { track, getGaIds } from "./analytics";
import { USE_MOCKS, TOS_VERSION, SUBMISSION_SOURCE } from "./env";

/**
 * Backend seam. Wraps the callable Cloud Functions (App Check enforced).
 *
 * Dev defaults to USE_MOCKS so the UI runs standalone. Set VITE_USE_EMULATORS=true
 * to hit the real callables against the Functions emulator. Production always
 * uses real calls. See ./env and ./firebase.
 */

/** NEC 220.82 branch-circuit counts, no longer collected from the customer. */
const DEFAULT_KITCHEN_CIRCUITS = 2;
const DEFAULT_LAUNDRY_CIRCUITS = 1;

export interface UploadUrlResult {
  submissionId: string;
  gcsUri: string;
  uploadUrl: string;
  objectPath: string;
}

export async function createUploadUrl(input: {
  submissionId: string;
  file: File;
  /** What this upload is; shapes the GCS object name for reviewers. Defaults to bill. */
  kind?: "bill" | "panel_photo";
}): Promise<UploadUrlResult> {
  if (USE_MOCKS) {
    return {
      submissionId: input.submissionId,
      gcsUri: `gs://generator-sales-bills/${input.submissionId}/${input.file.name}`,
      uploadUrl: "https://example.invalid/dev-mock-upload",
      objectPath: `${input.submissionId}/${input.file.name}`,
    };
  }
  const fn = httpsCallable<
    { submissionId: string; fileName: string; mimeType: string; sizeBytes: number; kind?: string },
    UploadUrlResult
    // limitedUseAppCheckTokens: required by the server's consumeAppCheckToken
    // replay protection (H3) — sends a fresh single-use token per call.
  >(getFirebaseFunctions(), "createUploadUrl", { limitedUseAppCheckTokens: true });
  const { data } = await fn({
    submissionId: input.submissionId,
    fileName: input.file.name,
    mimeType: input.file.type,
    sizeBytes: input.file.size,
    kind: input.kind ?? "bill",
  });
  return data;
}

export async function uploadFileToGcs(uploadUrl: string, file: File): Promise<void> {
  if (USE_MOCKS) return; // no-op in dev
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "content-type": file.type },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
}

export interface TierCard {
  tier: TierId;
  name: string;
  description: string;
  sku: string;
  unitName: string;
  kw: number;
  nameplateKw: number;
  loadManagement: boolean;
  priceTotal: number;
  depositAmount: number;
  recommended: boolean;
  lane: 1 | 2 | 3 | 4;
  disposition: "checkout" | "assessment" | "sales";
  laneReasons: string[];
  boxUpsizeDeltaUsd: number;
  gasLineAllowanceUsd: number;
}

export interface AdderOption {
  id: string;
  name: string;
  category?: string;
  unitPrice: number;
  pricing: "fixed" | "estimate";
  quantityBased: boolean;
  unitLabel?: string;
  maxQty?: number;
  note?: string;
}

export interface MembershipOffer {
  id: string;
  name: string;
  tagline?: string;
  annualPriceUsd: number;
  firstYearIncluded: boolean;
  benefits: string[];
  termsNote?: string;
}

export interface AdderLine {
  id: string;
  name: string;
  unitPrice: number;
  qty: number;
  lineTotal: number;
}

export interface RecommendResult {
  estimatedPeakKw: number;
  generatorFuel: "propane" | "natural_gas";
  recommendationBlurb: string;
  tiers: TierCard[];
  adders: AdderOption[];
  autoAdders: AdderLine[];
  baseIncludes: string[];
  gasWorkLabel: string;
  membership: MembershipOffer | null;
}

/** Step 3 -> 4: server sizing + AI. Persists a draft, returns tier cards. */
export async function recommend(state: FormState): Promise<RecommendResult> {
  if (USE_MOCKS) {
    return {
      estimatedPeakKw: 20.0,
      generatorFuel: "propane",
      recommendationBlurb:
        "Based on your home we estimate a whole-home load around 20 kW (NEC 220.82). Whole Home Managed right-sizes the unit with smart load management.",
      tiers: [
        { tier: "essentials", name: "Essentials", description: "Backs up critical circuits only.", sku: "GEN-10KW", unitName: "Generac 10 kW Next-Gen Air-Cooled (7321)", kw: 10, nameplateKw: 10, loadManagement: false, priceTotal: 11095, depositAmount: 500, recommended: false, lane: 1, disposition: "checkout", laneReasons: [], boxUpsizeDeltaUsd: 0, gasLineAllowanceUsd: 0 },
        { tier: "whole_home_managed", name: "Whole Home Managed", description: "Whole home with smart load management.", sku: "GEN-18KW", unitName: "Generac 18 kW Next-Gen Air-Cooled (7324)", kw: 18, nameplateKw: 18, loadManagement: true, priceTotal: 15395, depositAmount: 500, recommended: true, lane: 1, disposition: "checkout", laneReasons: [], boxUpsizeDeltaUsd: 0, gasLineAllowanceUsd: 0 },
        { tier: "whole_home_premium", name: "Whole Home Premium", description: "Whole home, no load shedding.", sku: "GEN-26KW", unitName: "Generac 26 kW Next-Gen Air-Cooled (7328)", kw: 26, nameplateKw: 26, loadManagement: false, priceTotal: 17395, depositAmount: 500, recommended: false, lane: 3, disposition: "checkout", laneReasons: ["Natural-gas unit at 26 kW may exceed a standard 250 CFH meter — gas capacity verified on site."], boxUpsizeDeltaUsd: 0, gasLineAllowanceUsd: 1500 },
      ],
      adders: [
        { id: "warranty_10yr", name: "Extended warranty upgrade (5 → 10 yr)", category: "warranty", unitPrice: 1449, pricing: "fixed", quantityBased: false, unitLabel: "each", maxQty: 1 },
        { id: "genpad_composite", name: "Composite GenPad upgrade", category: "electrical", unitPrice: 385, pricing: "fixed", quantityBased: false, unitLabel: "each", maxQty: 1 },
      ],
      autoAdders: [],
      baseIncludes: [
        "Cold-weather kit (battery + oil heater warmer) — standard on every install",
        "GenPad",
        "Permits & inspection",
        "Startup, commissioning & Mobile Link setup",
        "Year-1 annual service",
        "5-year Generac warranty",
      ],
      gasWorkLabel: "NG service/meter upsizing",
      membership: {
        id: "generator_care_member",
        name: "Generator Care Membership",
        tagline: "Become a member and get priority emergency service.",
        annualPriceUsd: 399,
        firstYearIncluded: true,
        benefits: [
          "Priority emergency response",
          "Member emergency rates — discounted labor and parts",
          "Waived emergency dispatch fee",
          "Annual preventive-maintenance visit",
        ],
        termsNote: "Recurring annual membership; auto-renews. First year included; billing begins year 2. Terms apply.",
      },
    };
  }
  // limitedUseAppCheckTokens: required by the server's consumeAppCheckToken replay
  // protection (H3) — a fresh single-use token per call so re-runs (Back/Continue)
  // and legitimate retries aren't rejected as replays.
  const fn = httpsCallable<RecommendRequest, RecommendResult>(getFirebaseFunctions(), "recommend", {
    limitedUseAppCheckTokens: true,
  });
  // Capture GA4 ids here (#40) and let them ride `source` through recommend ->
  // draft -> submit -> WH-1, so n8n can fire the signed `purchase` against the
  // same GA user. Resilient: resolves nulls (never throws) when GA is absent.
  const analytics = await getGaIds();
  const { data } = await fn({
    submissionId: state.submissionId,
    customer: {
      firstName: state.firstName,
      lastName: state.lastName,
      email: state.email,
      phone: state.phone,
    },
    install: { street: state.street, city: state.city, state: state.state, zip: state.zip },
    loadModel: {
      sqft: state.sqft ?? 0,
      heatingFuel: state.heatingFuel ?? "other",
      acUnits: state.acUnits,
      // Hard-coded NEC 220.82 branch-circuit defaults. Correct for the vast majority
      // of homes (2 small-appliance + 1 laundry); edge cases handled via change order.
      kitchenCircuits: DEFAULT_KITCHEN_CIRCUITS,
      laundryCircuits: DEFAULT_LAUNDRY_CIRCUITS,
      coolingTons: state.coolingTons,
      wellPump: state.wellPump,
      largeLoads: state.largeLoads,
      generatorFuel: state.generatorFuel,
      serviceAmps: state.serviceAmps,
      gasMainAvailable: state.gasMainAvailable,
      willingToSetPropaneTank: state.willingToSetPropaneTank,
      annualKwh: null,
    },
    bill: state.bill.gcsUri
      ? {
          gcsUri: state.bill.gcsUri,
          mimeType: state.bill.mimeType ?? "application/pdf",
          sizeBytes: state.bill.sizeBytes ?? 0,
          objectPath: state.bill.objectPath ?? "",
        }
      : null,
    panelPhoto: state.panelPhoto.gcsUri
      ? {
          gcsUri: state.panelPhoto.gcsUri,
          mimeType: state.panelPhoto.mimeType ?? "image/jpeg",
          sizeBytes: state.panelPhoto.sizeBytes ?? 0,
          objectPath: state.panelPhoto.objectPath ?? "",
        }
      : null,
    source: { ...SUBMISSION_SOURCE, analytics },
  });
  track("recommendation_generated", {
    submission_id: state.submissionId,
    estimated_peak_kw: data.estimatedPeakKw,
    generator_fuel: data.generatorFuel,
    tier_count: data.tiers.length,
    recommended_tier: data.tiers.find((t) => t.recommended)?.tier ?? "none",
  });
  // GA4-recommended `view_item_list` so the offered packages show up as products
  // in the funnel — the basis for product-level audiences in Google Ads.
  track("view_item_list", {
    submission_id: state.submissionId,
    item_list_name: "generator_tiers",
    items: data.tiers.map((t) => ({
      item_id: t.sku,
      item_name: t.name,
      item_category: t.tier,
      price: t.priceTotal,
    })),
  });
  return data;
}

interface RecommendRequest {
  submissionId: string;
  customer: { firstName: string; lastName: string; email: string; phone: string };
  install: { street: string; city: string; state: string; zip: string };
  loadModel: {
    sqft: number;
    heatingFuel: string;
    acUnits: number;
    kitchenCircuits: number;
    laundryCircuits: number;
    coolingTons: number;
    wellPump: boolean;
    largeLoads: string[];
    generatorFuel: string;
    serviceAmps: number | null;
    gasMainAvailable: boolean;
    willingToSetPropaneTank: boolean;
    annualKwh: number | null;
  };
  bill: { gcsUri: string; mimeType: string; sizeBytes: number; objectPath: string } | null;
  panelPhoto: { gcsUri: string; mimeType: string; sizeBytes: number; objectPath: string } | null;
  source: SubmissionSource;
}

export interface SubmitResult {
  submissionId: string;
  envelopeId?: string;
  status: string;
}

export interface AdderSelection {
  id: string;
  qty: number;
}

/** Step 4 -> 5 (confirm): fires WH-1 for the chosen tier + adders + membership opt-in. */
export async function submit(input: {
  submissionId: string;
  selectedTier: TierId;
  selectedAdders?: AdderSelection[];
  membershipOptIn?: boolean;
  esignConsent: boolean;
  /** When the customer actively checked e-sign consent; falls back to now. */
  esignConsentAt?: string | null;
}): Promise<SubmitResult> {
  if (USE_MOCKS) {
    return { submissionId: input.submissionId, envelopeId: "dev-envelope", status: "contract_sent" };
  }
  const fn = httpsCallable<
    {
      submissionId: string;
      selectedTier: TierId;
      selectedAdders?: AdderSelection[];
      membershipOptIn?: boolean;
      consent: { esignConsent: boolean; esignConsentAt: string; tosVersion: string };
    },
    SubmitResult
    // Explicit client timeout tied to the shared submit budget (#14): the server
    // bounds its n8n retries to finish well inside this window, so the client
    // never aborts a submission that actually succeeded server-side.
  >(getFirebaseFunctions(), "submit", { timeout: SUBMIT_CLIENT_TIMEOUT_MS });
  const { data } = await fn({
    submissionId: input.submissionId,
    selectedTier: input.selectedTier,
    selectedAdders: input.selectedAdders ?? [],
    membershipOptIn: input.membershipOptIn ?? false,
    consent: {
      esignConsent: input.esignConsent,
      esignConsentAt: input.esignConsentAt ?? new Date().toISOString(),
      tosVersion: TOS_VERSION,
    },
  });
  // `contract_sent` — a DocuSign envelope is on its way. This is a key event
  // (mid-funnel), NOT the revenue `purchase`; the authoritative value-bearing
  // purchase fires server-side (n8n) once the envelope is signed.
  track("contract_sent", {
    submission_id: input.submissionId,
    selected_tier: input.selectedTier,
    adder_count: input.selectedAdders?.length ?? 0,
    membership_opt_in: input.membershipOptIn ? 1 : 0,
    status: data.status,
  });
  return data;
}
